import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import {
  LEGACY_CAPABILITY_ID,
  type MachineDatasheet,
  type MachinePark,
} from '@indutrust/factory-model';
import {
  legacyToMachinePark,
  machineParkToLegacy,
} from '@indutrust/factory-util';
import { EnvironmentService } from '@indutrust/shared-env';
import { BaseApiClient } from '@indutrust/shared-model';
import type { Observable } from 'rxjs';

/**
 * API client for factory-scoped machine park entries under the legacy
 * capability-nested route —
 * `/factories/{factoryId}/capabilities/{capabilityId}/machine_parks`.
 *
 * {@link LEGACY_CAPABILITY_ID} is baked into the URL so callers keep passing
 * only `{ factoryId }` as `urlSegments`.
 *
 * Datasheet upload/delete stay on the flat `/factories/{factoryId}/machine_parks/...`
 * path (no legacy equivalent — served by MSW gap handlers in local dev).
 */
@Service()
export class MachineParkApiClient extends BaseApiClient<MachinePark> {
  protected envService = inject(EnvironmentService);
  protected http = inject(HttpClient);

  protected template: Partial<MachinePark> = {};

  protected override fromWire(wire: unknown): MachinePark {
    return legacyToMachinePark(wire);
  }

  protected override toWire(entity: Partial<MachinePark>): unknown {
    return machineParkToLegacy(entity);
  }

  /**
   * Deletes a specific datasheet from a machine.
   *
   * @example
   * client.deleteDatasheet(42, 7, 12).subscribe();
   */
  deleteDatasheet(
    factoryId: number,
    machineId: number,
    datasheetId: number
  ): Observable<void> {
    const api = this.envService.getApiUrl();
    return this.http.delete<void>(
      `${api}/factories/${factoryId}/machine_parks/${machineId}/datasheets/${datasheetId}`
    );
  }

  /**
   * Uploads a datasheet file for a specific machine using `multipart/form-data`.
   *
   * @example
   * client.uploadDatasheet(42, 7, file).subscribe();
   */
  uploadDatasheet(
    factoryId: number,
    machineId: number,
    file: File
  ): Observable<MachineDatasheet> {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('name', file.name);
    const api = this.envService.getApiUrl();
    return this.http.post<MachineDatasheet>(
      `${api}/factories/${factoryId}/machine_parks/${machineId}/datasheets`,
      form
    );
  }

  get url(): string {
    return `${this.envService.getApiUrl()}/factories/{factoryId}/capabilities/${LEGACY_CAPABILITY_ID}/machine_parks`;
  }
}
